import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { extractErrorMessage, isObject } from 'src/helper/utils';
import {
  approvedUnderReview,
  getVendorDetails,
  getVendors,
  getVendorWorkApprovals,
  updateVendorWorkApproval,
} from 'src/redux/vendors/api';
import type {
  InitialStateType,
  VendorDataType,
  VendorWorkApprovalRow,
} from 'src/redux/vendors/types';

const LIST_LOAD_FAILED = "Couldn't load the vendors waiting for approval.";

// Vendors waiting for approval (6.11). Only the latest list request may write: an aborted one
// (the page re-mounted, or Retry) resolves after its successor has started.
const workApprovalReducers = (builder: ActionReducerMapBuilder<InitialStateType>) => {
  builder.addCase(getVendorWorkApprovals().pending, (state, action) => {
    state.workApprovals.isLoading = true;
    state.workApprovals.loadError = null;
    state.workApprovals.requestId = action.meta.requestId;
  });
  builder.addCase(getVendorWorkApprovals().fulfilled, (state, action) => {
    if (state.workApprovals.requestId !== action.meta.requestId) {
      return;
    }
    const rows: unknown = action.payload;
    state.workApprovals.rows = Array.isArray(rows) ? (rows as VendorWorkApprovalRow[]) : [];
    state.workApprovals.isLoading = false;
    state.workApprovals.hasLoaded = true;
  });
  builder.addCase(getVendorWorkApprovals().rejected, (state, action) => {
    if (state.workApprovals.requestId !== action.meta.requestId) {
      return;
    }
    state.workApprovals.isLoading = false;
    if ((action.payload as { actionType?: string } | undefined)?.actionType === 'canceled') {
      return;
    }
    state.workApprovals.loadError = extractErrorMessage(action.payload, LIST_LOAD_FAILED);
  });
  // A decided vendor leaves the pending list; the server answers with its updated row.
  builder.addCase(updateVendorWorkApproval().fulfilled, (state, action) => {
    const row = action.payload as VendorWorkApprovalRow | undefined;
    if (!row?.vendor_uuid || row.work_approval === 'pending') {
      return;
    }
    state.workApprovals.rows = state.workApprovals.rows.filter(
      (pending) => pending.vendor_uuid !== row.vendor_uuid,
    );
  });
};

const extraReducers = (builder: ActionReducerMapBuilder<InitialStateType>) => {
  workApprovalReducers(builder);

  builder.addCase(getVendors().pending, (state: InitialStateType) => {
    state.isLoading = true;
  });
  builder.addCase(getVendors().fulfilled, (state: InitialStateType, action) => {
    if (Number(action.meta.arg.page || 0) > 1) {
      // data will be appending to the previous data.
      const usersListData = [
        ...(state?.data?.data || []),
        ...(action?.payload?.data?.map((item: VendorDataType) => item?.vendor_id) || []),
      ];
      if (action?.payload) {
        state.data = {
          data: usersListData,
          meta: action?.payload?.meta || {},
        };
      }
    } else {
      state.data = {
        data: action?.payload?.data?.map((item: VendorDataType) => item?.vendor_id),
        meta: action?.payload?.meta || {},
      };
    }
    const newSource = { ...(state?.source || {}) };
    action?.payload?.data?.forEach((item: VendorDataType) => {
      let newData = { ...(newSource?.[item?.vendor_id] || {}) };

      const itemKeys = Object.keys(item) as Array<keyof typeof item>;

      itemKeys?.forEach((fieldKey) => {
        if (isObject(item?.[fieldKey]) && isObject(newData[fieldKey])) {
          newData = {
            ...newData,
            [fieldKey]: {
              ...((newData?.[fieldKey] || {}) as Record<string, any>),
              ...((item?.[fieldKey] || {}) as Record<string, any>),
            },
          };
        } else {
          newData = {
            ...newData,
            [fieldKey]: item?.[fieldKey],
          };
        }
      });

      newSource[item?.vendor_id] = newData;
    });
    state.isLoading = false;
    state.source = newSource;
    if (action?.payload?.property_management_system) {
      state.propertyManagementSystem = action.payload.property_management_system;
    }
  });
  builder.addCase(getVendors().rejected, (state: InitialStateType) => {
    state.isLoading = false;
  });

  // Get vendor details
  builder.addCase(getVendorDetails().pending, (state: InitialStateType) => {
    state.isDetailsLoading = true;
  });

  builder.addCase(getVendorDetails().fulfilled, (state: InitialStateType, action) => {
    state.isDetailsLoading = false;
    state.source = {
      ...(state?.source || {}),
      [action?.payload?.vendor_id]: action?.payload,
    };
  });

  builder.addCase(getVendorDetails().rejected, (state: InitialStateType) => {
    state.isDetailsLoading = false;
  });

  builder.addCase(approvedUnderReview().fulfilled, (state: InitialStateType, action) => {
    const updatedVendor = {
      ...(state?.source?.[action?.payload?.vendor_id] || {}),
      onboarding: {
        vendor_onboarding_id: action?.payload?.onboarding_id || '',
        status: {
          label: action?.payload?.status?.name || '',
          slug: action?.payload?.status?.slug || '',
        },
      },
    };
    state.source = {
      ...(state?.source || {}),
      [action?.payload?.vendor_id]: updatedVendor,
    };
  });
};

export default extraReducers;
